/**
 * 工作流执行进度条组件
 *
 * 显示在工作流画布顶部，实时展示执行进度
 * 包括：进度条、完成节点数/总节点数、当前执行节点名称
 */

import React, { memo, useMemo } from 'react';
import { clsx } from 'clsx';
import { useExecutionStore } from '../../store/workflowStore';
import { useWorkflowStore } from '../../store/workflowStore';
import { ExecutionState, NodeExecutionStatus } from '../../types/workflow';
import { getProgressColor } from './styles/nodeStatusStyles';

/**
 * 执行状态徽章组件
 */
const ExecutionStateBadge: React.FC<{ state: ExecutionState }> = memo(({ state }) => {
  const stateConfig: Record<ExecutionState, { label: string; bgClass: string; textClass: string }> = {
    [ExecutionState.IDLE]: {
      label: '空闲',
      bgClass: 'bg-gray-100',
      textClass: 'text-gray-600',
    },
    [ExecutionState.RUNNING]: {
      label: '执行中',
      bgClass: 'bg-paper-100',
      textClass: 'text-paper-800',
    },
    [ExecutionState.PAUSED]: {
      label: '已暂停',
      bgClass: 'bg-warning-100',
      textClass: 'text-warning-700',
    },
    [ExecutionState.STEP_WAITING]: {
      label: '单步等待',
      bgClass: 'bg-paper-100',
      textClass: 'text-paper-800',
    },
    [ExecutionState.COMPLETED]: {
      label: '已完成',
      bgClass: 'bg-success-100',
      textClass: 'text-success-700',
    },
    [ExecutionState.FAILED]: {
      label: '失败',
      bgClass: 'bg-error-100',
      textClass: 'text-error-700',
    },
    [ExecutionState.INTERRUPTED]: {
      label: '已中断',
      bgClass: 'bg-gray-100',
      textClass: 'text-gray-600',
    },
  };

  const config = stateConfig[state] || stateConfig[ExecutionState.IDLE];

  return (
    <span className={clsx(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
      config.bgClass,
      config.textClass,
    )}>
      {state === ExecutionState.RUNNING && (
        <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )}
      {config.label}
    </span>
  );
});

ExecutionStateBadge.displayName = 'ExecutionStateBadge';

/**
 * 进度条组件
 */
const ProgressBar: React.FC<{
  progress: number;
  state: ExecutionState;
}> = memo(({ progress, state }) => {
  const colorClass = getProgressColor(state === ExecutionState.PAUSED ? 'paused' : 
    state === ExecutionState.COMPLETED ? NodeExecutionStatus.COMPLETED :
    state === ExecutionState.FAILED ? NodeExecutionStatus.FAILED :
    NodeExecutionStatus.RUNNING);

  return (
    <div className="workflow-progress-bar flex-1 max-w-xs">
      <div
        className={clsx('workflow-progress-bar-fill', colorClass)}
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  );
});

ProgressBar.displayName = 'ProgressBar';

/**
 * 执行进度条主组件
 */
export interface ExecutionProgressBarProps {
  /** 是否显示状态徽章 */
  showStateBadge?: boolean;
  /** 是否显示当前节点名称 */
  showCurrentNode?: boolean;
  /** 是否显示进度百分比 */
  showPercentage?: boolean;
  /** 自定义类名 */
  className?: string;
}

export const ExecutionProgressBar: React.FC<ExecutionProgressBarProps> = memo(({
  showStateBadge = true,
  showCurrentNode = true,
  showPercentage = true,
  className,
}) => {
  // 从 store 获取执行状态
  const executionState = useExecutionStore((state) => state.executionState);
  const currentNodeId = useExecutionStore((state) => state.currentNodeId);
  const progress = useExecutionStore((state) => state.progress);
  const error = useExecutionStore((state) => state.error);

  // 从 workflow store 获取节点信息
  const getNodeById = useWorkflowStore((state) => state.getNodeById);

  // 计算进度百分比
  const progressPercent = useMemo(() => {
    if (progress.total === 0) return 0;
    return Math.round((progress.completed / progress.total) * 100);
  }, [progress.completed, progress.total]);

  // 获取当前节点名称
  const currentNodeName = useMemo(() => {
    if (!currentNodeId) return null;
    const node = getNodeById(currentNodeId);
    if (!node) return null;
    return (node.data as Record<string, unknown>)?.label as string || currentNodeId;
  }, [currentNodeId, getNodeById]);

  // 是否显示进度条（非空闲状态时显示）
  const showProgress = executionState !== ExecutionState.IDLE;

  if (!showProgress) {
    return null;
  }

  return (
    <div className={clsx(
      'flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200 shadow-sm',
      className,
    )}>
      {/* 状态徽章 */}
      {showStateBadge && (
        <ExecutionStateBadge state={executionState} />
      )}

      {/* 进度条 */}
      <ProgressBar progress={progressPercent} state={executionState} />

      {/* 进度数字 */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span className="font-medium">
          {progress.completed}/{progress.total}
        </span>
        {showPercentage && (
          <span className="text-gray-400">
            ({progressPercent}%)
          </span>
        )}
      </div>

      {/* 分隔线 */}
      {showCurrentNode && currentNodeName && (
        <div className="h-4 w-px bg-gray-200" />
      )}

      {/* 当前执行节点 */}
      {showCurrentNode && currentNodeName && (
        <div className="flex items-center gap-2 text-sm">
          <svg className="w-4 h-4 text-paper-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-gray-700 truncate max-w-[150px]">
            {currentNodeName}
          </span>
        </div>
      )}

      {/* 错误信息 */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-error-600 ml-auto">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="truncate max-w-[200px]">{error}</span>
        </div>
      )}
    </div>
  );
});

ExecutionProgressBar.displayName = 'ExecutionProgressBar';

export default ExecutionProgressBar;
